/**
 * Central feature-flag manager. One place for every gated feature — import
 * `FEATURE_FLAGS` and read `FEATURE_FLAGS.<name>` instead of scattering
 * one-off *_ENABLED constants across the tree.
 *
 * Each flag resolves to a boolean once, at module load. Some are static; some
 * are environment-derived. The shared ENV helpers below keep every env-derived
 * flag in agreement about what "local" / "staging" / "prod" mean (mirrors the
 * detection in DynamoConnector.getClientDomainPrefix).
 *
 * To add a flag: add one entry to `definitions` with a short comment on what it
 * gates and when it should flip.
 */

const host = typeof window !== 'undefined' && window.location ? window.location.hostname : '';

const isLocal = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.');
const isStaging = host.includes('staging.commandersalt.com');
const isProd = !isLocal && !isStaging;

export const ENV = Object.freeze({ isLocal, isStaging, isProd });

/**
 * Env booleans used to RESOLVE FEATURE FLAGS — deliberately distinct from `ENV`.
 * Localhost is treated exactly like prod here so local dev mirrors what actually
 * ships (no flags silently on locally but off in prod). Staging keeps its own
 * looser values for QA. Use these in `definitions` below; use `ENV` everywhere
 * else, where the true host still matters.
 */
const flagEnv = Object.freeze({
    isStaging,
    isProd: isProd || isLocal,
});

// Flip to true (or wire to a build env var) to launch ads in production.
const ADS_PROD_OVERRIDE = true;

const definitions = {
    /**
     * NitroPay ad integration and its compliance surface:
     *   - /privacy route + Privacy Policy page (required by Google to serve ads)
     *   - footer CCPA/GDPR compliance links in the About (InfoDialog) popover
     *   - the NitroPay loader script (ads-2448.js) injected by src/ads/nitroPay.js
     *   - (next) ad placements via nitroAds.createAd, once the dashboard
     *     Placement Builder configs + mount points are decided
     *
     * ON for localhost + staging so the surface can be developed and QA'd, OFF
     * in prod until launch. (localhost can't serve real NitroPay ads — the
     * domain isn't registered — so NitroAd renders a `demo` placeholder there;
     * see src/ads/nitroPay.js. Staging serves real ads.) To go live in prod, set
     * ADS_PROD_OVERRIDE = true; everything gated by this flag lights up together.
     *
     * NOTE: the /ads.txt 301 redirect is CloudFront infra, not client code, so
     * it can't be toggled here — see the infra requirements doc handed to the
     * API/infra agents.
     */
    ads: isLocal || flagEnv.isStaging || ADS_PROD_OVERRIDE,

    /**
     * Privacy Policy surface that must be live BEFORE ads — Google requires a
     * reachable privacy policy to approve the ad account:
     *   - /privacy route + Privacy Policy page
     *   - static "Privacy Policy" link in the footer (About/InfoDialog popover)
     *
     * Always ON (every env) and intentionally decoupled from `ads`: the policy
     * page and its footer link have no dependency on the NitroPay script. The
     * CMP-dependent controls ("Do Not Sell", "Cookie & Consent Settings") stay
     * behind `ads` because they need NitroPay's CMP to do anything.
     */
    privacyPolicy: true,

    /**
     * /upgrades base-search overhaul UI. When the backend ships
     * PLAN_upgrades_base_search_overhaul (advanced filters, matches[]/matchCount/
     * matchesTruncated, /upgrades/match-detail, 503/504/invalid_cursor codes),
     * flip this to true.
     *
     * The transport + slice + error-classification changes ship live regardless
     * — they degrade gracefully against the old endpoint shape. This flag only
     * gates the surface that would be broken without the new API: the Advanced
     * Filters panel, the inline match-summary chip, and the "+N more" match link.
     */
    upgradesBaseSearchOverhaul: true,
};

export const FEATURE_FLAGS = Object.freeze(definitions);

/** Stringly-typed accessor for dynamic lookups; prefer FEATURE_FLAGS.<name>. */
export function isFeatureEnabled(name) {
    return Boolean(FEATURE_FLAGS[name]);
}
